import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import { Button, IconButton } from "../../ui/UIElements/Button";
import "./campaigns-ordering.css";
import { useCampaignDetail } from "../../../hooks/useCampaigns";
import {
  getContactTypeBadge,
  getCampaignName,
  hasDetailedStats,
  getCampaignStatusDisplay,
  stripHtml,
  getTimeAgo,
  extractButtonData,
} from "../../../constants/campaignConstants";
import {
  formatDateByLocale,
  formatNumberByLocale,
} from "../../../lib/localeFormatters";
import { useUiLocale } from "../../../hooks/useUiLocale";

const CampaignDetailPage = ({ initialCampaign = null }) => {
  const { t } = useTranslation("campaigns");
  const { locale } = useUiLocale();
  const { id } = useParams();
  const campaignId = parseInt(id);
  const navigate = useNavigate();
  const location = useLocation();

  const campaignFromState = location.state?.campaign;
  const campaignData = initialCampaign || campaignFromState;

  const {
    data: campaign,
    isLoading,
    error,
    isError,
  } = useCampaignDetail(campaignId, campaignData);

  const handleBackToCampaigns = () => {
    navigate("/campaigns");
  };

  // Format date (converts UTC to local time, unless isLocalDate is true)
  const formatDate = (dateString, isLocalDate = false) => {
    if (!dateString) return t("detail.notScheduled");

    let dateToFormat;
    if (isLocalDate) {
      // For local dates (like end_at), interpret as-is without timezone conversion
      dateToFormat = dateString.replace(" ", "T");
    } else {
      // Ensure the date is interpreted as UTC by adding 'Z' if not present
      let utcDateString = dateString;
      if (!dateString.endsWith("Z") && !dateString.includes("+")) {
        utcDateString = dateString.replace(" ", "T") + "Z";
      }
      dateToFormat = utcDateString;
    }

    const date = new Date(dateToFormat);
    return formatDateByLocale(
      date,
      {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      },
      locale,
    );
  };

  if (isLoading) {
    return (
      <div
        className="ordering-campaigns-surface flex min-h-96 items-center justify-center p-4"
        role="status"
        aria-live="polite"
      >
        <div className="text-center">
          <div className="border-primary mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 sm:mb-4 sm:h-12 sm:w-12"></div>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t("detail.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !campaign)) {
    return (
      <div
        className="ordering-campaigns-surface px-4 py-8 text-center sm:py-12"
        role="alert"
      >
        <Icon
          name="Megaphone"
          className="text-muted-foreground mx-auto mb-3 h-8 w-8 sm:mb-4 sm:h-12 sm:w-12"
        />
        <h3 className="text-muted-foreground mt-2 text-sm font-medium sm:text-base">
          {t("detail.error.title")}
        </h3>
        <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
          {error?.message || t("detail.error.subtitle")}
        </p>
        <Button
          type="button"
          emphasis="solid"
          intent="brand"
          onClick={handleBackToCampaigns}
          className="mt-3 sm:mt-4"
        >
          {t("detail.actions.backToCampaigns")}
        </Button>
      </div>
    );
  }

  const contactTypeBadge = getContactTypeBadge(campaign.contact_type);
  const showDetailedStats = hasDetailedStats(campaign.contact_type);

  const sentCount = campaign.sent_count || 0;
  const openCount = campaign.open_count || 0;
  const clickCount = campaign.clicked_count || 0;
  const openRate =
    sentCount > 0 ? ((openCount / sentCount) * 100).toFixed(1) : "0.0";
  const clickRate =
    sentCount > 0 ? ((clickCount / sentCount) * 100).toFixed(1) : "0.0";

  const statusDisplay = getCampaignStatusDisplay(campaign.status);

  const subject =
    campaign.contact_data?.title || campaign.contact_data?.subject || "";
  const body =
    campaign.contact_data?.body || campaign.contact_data?.message || "";
  const bodyText = stripHtml(body);
  const buttonData = extractButtonData(body);

  return (
    <div
      className="ordering-campaigns-surface ordering-campaign-detail space-y-6"
      data-ordering-expression="product"
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <IconButton
          type="button"
          emphasis="ghost"
          intent="neutral"
          onClick={handleBackToCampaigns}
          aria-label={t("detail.actions.backToCampaigns")}
        >
          <Icon name="ArrowLeft" className="text-muted-foreground" />
        </IconButton>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-foreground text-xl font-bold sm:text-2xl">
              {getCampaignName(campaign)}
            </h1>
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium ${statusDisplay.bgColor} ${statusDisplay.textColor}`}
            >
              {statusDisplay.label}
            </span>
          </div>
          <p className="text-muted-foreground mt-1 flex items-center gap-1 text-sm">
            <Icon name="Clock" className="h-3 w-3" />
            {t("detail.lastUpdated")} {getTimeAgo(campaign.updated_at)}
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Left Column - Stats and Info */}
        <div className="space-y-6 xl:col-span-2">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Total Sent */}
            <div className="border-border bg-background rounded-xl border p-5">
              <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                {t("stats.totalSent")}
              </p>
              <div className="flex items-baseline">
                <span className="text-foreground text-2xl font-bold">
                  {sentCount >= 1000
                    ? `${(sentCount / 1000).toFixed(
                        sentCount >= 10000 ? 0 : 1,
                      )}k`
                    : formatNumberByLocale(sentCount, {}, locale)}
                </span>
              </div>
              <div className="bg-card mt-3 h-1.5 overflow-hidden rounded-full">
                <div
                  className="bg-primary h-full rounded-full"
                  style={{ width: "100%" }}
                ></div>
              </div>
            </div>

            {/* Open Rate */}
            <div className="border-border bg-background rounded-xl border p-5">
              <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                {t("table.metrics.openRate")}
              </p>
              <div className="flex items-baseline">
                <span className="text-foreground text-2xl font-bold">
                  {showDetailedStats ? `${openRate}%` : "-"}
                </span>
              </div>
              <div className="bg-card mt-3 h-1.5 overflow-hidden rounded-full">
                <div
                  className="bg-primary h-full rounded-full"
                  style={{ width: showDetailedStats ? `${openRate}%` : "0%" }}
                ></div>
              </div>
            </div>

            {/* Click Rate */}
            <div className="border-border bg-background rounded-xl border p-5">
              <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                {t("table.metrics.ctr")}
              </p>
              <div className="flex items-baseline">
                <span className="text-foreground text-2xl font-bold">
                  {showDetailedStats ? `${clickRate}%` : "-"}
                </span>
              </div>
              <div className="bg-card mt-3 h-1.5 overflow-hidden rounded-full">
                <div
                  className="bg-success h-full rounded-full"
                  style={{
                    width: showDetailedStats
                      ? `${Math.min(parseFloat(clickRate) * 2, 100)}%`
                      : "0%",
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Campaign Configuration */}
          <div className="border-border bg-background rounded-xl border">
            <div className="border-border border-b p-4">
              <div className="flex items-center gap-2">
                <Icon name="BarChart3" className="text-primary h-5 w-5" />
                <h2 className="text-foreground text-base font-semibold">
                  {t("detail.configuration.title")}
                </h2>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Channel */}
                <div>
                  <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                    {t("detail.configuration.channel")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon
                      name={contactTypeBadge.icon}
                      className={`h-4 w-4 ${contactTypeBadge.text}`}
                    />
                    <span className="text-foreground capitalize">
                      {campaign.contact_type || t("table.fallback.email")}
                    </span>
                  </div>
                </div>

                {/* Target Audience */}
                <div>
                  <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                    {t("detail.configuration.targetAudience")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon
                      name="Users"
                      className="text-muted-foreground h-4 w-4"
                    />
                    <span className="text-foreground">
                      {campaign.audience_name ||
                        t("detail.configuration.allSubscribers")}
                    </span>
                  </div>
                </div>

                {/* Created At */}
                <div>
                  <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                    {t("detail.configuration.createdAt")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon
                      name="Calendar"
                      className="text-muted-foreground h-4 w-4"
                    />
                    <span className="text-foreground">
                      {formatDate(campaign.created_at)}
                    </span>
                  </div>
                </div>

                {/* End Date */}
                <div>
                  <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                    {t("detail.configuration.endDate")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon
                      name="Calendar"
                      className="text-muted-foreground h-4 w-4"
                    />
                    <span className="text-foreground">
                      {formatDate(campaign.end_at, true)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="border-border bg-background rounded-xl border">
            <div className="border-border border-b p-4">
              <div className="flex items-center gap-2">
                <Icon name="Mail" className="text-primary h-5 w-5" />
                <h2 className="text-foreground text-base font-semibold">
                  {t("detail.content.title")}
                </h2>
              </div>
            </div>
            <div className="space-y-4 p-4">
              {/* Subject Line */}
              {subject && (
                <div>
                  <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                    {t("detail.content.subjectLine")}
                  </p>
                  <p className="text-foreground">{subject}</p>
                </div>
              )}

              {/* Body Text */}
              {bodyText && (
                <div>
                  <p className="text-muted-foreground mb-2 text-xs tracking-wider uppercase">
                    {t("detail.content.bodyText")}
                  </p>
                  <div className="bg-card overflow-hidden rounded-lg p-4">
                    <p className="overflow-wrap-anywhere text-muted-foreground font-mono text-sm break-words whitespace-pre-wrap">
                      {bodyText}
                    </p>
                  </div>
                </div>
              )}

              {!subject && !bodyText && (
                <div className="py-6 text-center">
                  <Icon
                    name="FileText"
                    className="text-muted-foreground mx-auto mb-2 h-10 w-10"
                  />
                  <p className="text-muted-foreground text-sm">
                    {t("detail.content.empty")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Device Preview */}
        <div className="ordering-generated-preview xl:col-span-1">
          <div className="rounded-xl border border-gray-800 bg-gray-900 xl:sticky xl:top-6">
            <div className="border-b border-gray-800 p-4">
              <div className="flex items-center gap-2">
                <Icon name="Smartphone" className="h-5 w-5 text-gray-400" />
                <h2 className="text-base font-semibold text-white">
                  {t("detail.preview.title")}
                </h2>
              </div>
            </div>
            <div className="flex justify-center p-4">
              {/* Phone Frame */}
              <div className="w-full max-w-[280px]">
                <div className="rounded-[2.5rem] border-4 border-gray-800 bg-gray-950 p-2">
                  {/* Phone Notch */}
                  <div className="relative">
                    <div className="absolute top-0 left-1/2 z-10 flex h-6 w-20 -translate-x-1/2 transform items-center justify-center rounded-b-xl bg-gray-950">
                      <div className="h-2 w-2 rounded-full bg-gray-800"></div>
                      <div className="ml-1 h-2 w-2 rounded-full bg-gray-800"></div>
                    </div>

                    {/* Phone Screen */}
                    <div className="overflow-hidden rounded-[2rem] bg-white">
                      {/* Status Bar */}
                      <div className="flex items-center justify-between bg-gray-100 px-4 py-2 text-xs text-gray-600">
                        <span>9:41</span>
                        <div className="flex items-center gap-1">
                          <div className="h-2 w-4 rounded-sm bg-gray-400"></div>
                          <div className="h-2 w-4 rounded-sm bg-gray-400"></div>
                        </div>
                      </div>

                      {/* Email App Header */}
                      <div className="flex items-center justify-between bg-purple-600 px-4 py-3">
                        <Icon name="ArrowLeft" className="h-5 w-5 text-white" />
                        <span className="font-medium text-white">
                          {t("detail.preview.inbox")}
                        </span>
                        <Icon name="Search" className="h-5 w-5 text-white" />
                      </div>

                      {/* Email Content */}
                      <div className="min-h-[500px] p-4">
                        {/* Sender */}
                        <div className="mb-4 flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                            <span className="text-sm font-bold text-purple-600">
                              O
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              Ordering.co
                            </p>
                            <p className="text-xs text-gray-500">
                              {t("detail.preview.toMe")}
                            </p>
                          </div>
                          <span className="text-xs text-gray-400">
                            {t("detail.preview.now")}
                          </span>
                        </div>

                        {/* Email Subject */}
                        <h3 className="mb-3 text-sm font-semibold text-gray-900">
                          {subject || t("detail.preview.defaultSubject")}
                        </h3>

                        {/* Email Body Preview */}
                        <div className="space-y-2 text-xs text-gray-600">
                          {bodyText ? (
                            <p className="whitespace-pre-wrap">
                              {bodyText.substring(0, 200)}
                              {bodyText.length > 200 ? "..." : ""}
                            </p>
                          ) : (
                            <>
                              <p>{t("detail.preview.fallbackBodyLine1")}</p>
                              <p>{t("detail.preview.fallbackBodyLine2")}</p>
                              <p>{t("detail.preview.fallbackBodyLine3")}</p>
                            </>
                          )}
                        </div>

                        {/* CTA Button */}
                        <div className="mt-6 border-t border-gray-100 pt-4 text-center">
                          {buttonData?.url ? (
                            <a
                              href={buttonData.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block max-w-[200px] rounded-full px-8 py-3 text-xs font-bold text-white shadow-lg transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                              style={{ backgroundColor: buttonData.color }}
                            >
                              <span
                                className="block overflow-hidden"
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: "1.4",
                                }}
                              >
                                {buttonData.text}
                              </span>
                            </a>
                          ) : (
                            <span
                              className="inline-block max-w-[200px] rounded-full px-8 py-3 text-xs font-bold text-white shadow-lg"
                              style={{
                                backgroundColor: buttonData?.color || "#06b6d4",
                              }}
                            >
                              <span
                                className="block overflow-hidden"
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: "1.4",
                                }}
                              >
                                {buttonData?.text ||
                                  t("detail.preview.shopNow")}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetailPage;
